import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";
import { consentClientOptions } from "../../shared/mcp";
import type { Platform, TenantIdentity } from "../Platform";
import { createBrowserCapabilities } from "../browserCapabilities";
import { createBrowserPrefs } from "../createBrowserPrefs";
import { createBrowserAreasCapability } from "../localFamilies/areas";
import axios from "../../config/axios";
import { installWebNavigateHandler, openOnWeb } from "./webNavigation";
import { webClientIdentity } from "../../api/clientDeclaration";
import { WEB_MARVIN_RENDERS } from "../../api/marvinRenders";
import { canonicalScopeOrgSlug, embedOrganizationSlug } from "../tenantIdentity";

export async function createWebPlatform(): Promise<Platform> {
    const capabilities = createBrowserCapabilities();
    const stytchClient = new StytchB2BUIClient(
        process.env.REACT_APP_STYTCH_TOKEN!,
        consentClientOptions(window.location),
    );
    const tenant: TenantIdentity = {
        orgSlug() {
            const scoped = canonicalScopeOrgSlug();
            if (scoped !== undefined) return scoped;
            const organization = stytchClient.organization.getSync();
            if (organization) return organization.organization_slug;
            // useCompatOrganization writes this cache only for bearer-token embed auth.
            return embedOrganizationSlug();
        },
    };
    return {
        kind: "web",
        projectAdmission: {
            owner: "app",
            supportsCanonicalSelection: true,
            supportsUrlRouting: true,
        },
        bootstrap: {
            apiOrigin: window.location.origin,
            queryNetworkMode: "online",
        },
        open: openOnWeb,
        tenant,
        auth: {
            withCredentials: true,
            credentials: "include",
            stytchClient,
        },
        ...capabilities,
        pdfRenderer: "wasm",
        marvinRenders: WEB_MARVIN_RENDERS,
        marvinRenderer: "web",
        showsCollections: true,
        offersMobileApp: true,
        voiceInput: true,
        clientIdentity: webClientIdentity(process.env.REACT_APP_GIT_COMMIT),
        // The window's URL is the app's route. `open` routes a same-origin target through
        // the app router once the app lends its navigate; until then a same-tab open
        // falls back.
        navigation: {
            router: "browser",
            tabs: "browser",
            installNavigateHandler: installWebNavigateHandler,
        },
        // Areas use their own committed IndexedDB family store. This does not enable
        // the generic transport replay/answer-first paths.
        prefs: createBrowserPrefs(tenant, () => window.location.origin),
        ...(typeof indexedDB !== "undefined"
            ? {
                  offline: {
                      areas: createBrowserAreasCapability({
                          scope: () => {
                              const canonical = canonicalScopeOrgSlug();
                              // A project transition must not read the old organization-wide
                              // snapshot while canonical admission is pending or closed.
                              if (canonical === null) return null;
                              const organization = stytchClient.organization.getSync();
                              const member = stytchClient.member.getSync?.();
                              return organization?.organization_id && member?.member_id
                                  ? `${window.location.origin}:${canonical ?? organization.organization_id}:${member.member_id}`
                                  : null;
                          },
                          fetchAreas: async () => (await axios.get("/api/ai/areas")).data,
                      }),
                  },
              }
            : {}),
    };
}
